import { card, deadwoodValue, sortForDisplay } from './cards.js';
import { classifyMeld } from './melds.js';
import type { CardId, Group, MeldContext } from './types.js';

const INF = 1 << 28;

/*
 * The DP minimises a single packed key per arrangement:
 *
 *     key = (deadwood points << 4) | (ungrouped card count)
 *
 * Points and card count are *different* questions and the difference matters.
 * A wild left loose is worth zero points, so minimising points alone will
 * happily strand a joker outside every meld — and then call the hand declarable,
 * because it scores zero. Declaring requires all thirteen cards grouped (§3.3),
 * not merely a hand worth nothing.
 *
 * Packing them keeps one DP: an ungrouped count never exceeds 14, so it cannot
 * carry into the points field, and minimising the key minimises points first and
 * ungrouped cards second. A key of exactly 0 is the only thing that means "every
 * card is in a meld".
 */
const POINT_SHIFT = 4;
const COUNT_MASK = (1 << POINT_SHIFT) - 1;

const points = (key: number): number => key >>> POINT_SHIFT;
const ungrouped = (key: number): number => key & COUNT_MASK;

export interface Arrangement {
  /** Valid melds, best-first: pure sequences, then sequences, then sets. */
  groups: Group[];
  /** Cards in no meld under this arrangement. */
  deadwood: CardId[];
  sequences: number;
  pureSequences: number;
}

export interface SolveResult {
  /** Points this hand scores if the hand ends now and this player did not win. */
  points: number;
  /** Deadwood under the best fully-valid arrangement, or `null` if none exists. */
  declarableDeadwood: number | null;
  /** True when the hand already satisfies the winning condition with 0 deadwood. */
  canDeclare: boolean;
  arrangement: Arrangement;
  /** How many combinations short of the winning condition, for UI hinting. */
  sequencesShort: number;
  pureShort: number;
  /**
   * `deadwoodByState[p][s]` — the minimum deadwood *points* achievable having
   * formed exactly `p` pure sequences and `s` sequences, each capped at what the
   * winning condition requires. `Infinity` where that combination is
   * unreachable.
   *
   * `points` answers "what does this hand score"; the scoring tiers make that
   * function almost flat early in a hand, which is useless to a player trying
   * to decide what to throw. This matrix is the full picture the DP already
   * computed, and it is what the bot evaluates against.
   */
  deadwoodByState: number[][];
  /**
   * `ungroupedByState[p][s]` — how many cards are left outside a meld in that
   * same arrangement. Zero is what declaring actually requires; zero *points* is
   * not the same thing, because a loose wild costs nothing.
   */
  ungroupedByState: number[][];
}

interface Meld {
  mask: number;
  low: number;
  isSeq: boolean;
  isPure: boolean;
  value: number;
}

function popcount(x: number): number {
  let n = 0;
  while (x) {
    x &= x - 1;
    n++;
  }
  return n;
}

/**
 * Enumerates every valid meld inside a hand.
 *
 * Naively this is 2^13 classification calls. Instead a subset DP precomputes,
 * for every mask, the union of suits and ranks over the cards that can *only*
 * play at face identity. A sequence needs at most one suit among those; a set
 * needs at most one rank. That rejects the overwhelming majority of subsets in
 * two bit operations, leaving a few hundred candidates to classify properly.
 */
function enumerateMelds(hand: readonly CardId[], ctx: MeldContext): Meld[] {
  const n = hand.length;
  const total = 1 << n;
  const suitU = new Uint8Array(total);
  const rankU = new Uint16Array(total);
  const size = new Uint8Array(total);
  const value = new Int32Array(total);

  const cardSuitBit = new Uint8Array(n);
  const cardRankBit = new Uint16Array(n);
  const cardPoints = new Int32Array(n);
  const lowIndex = new Int8Array(total);

  const SUIT_BITS: Record<string, number> = { S: 1, H: 2, D: 4, C: 8 };
  const RANK_ORDER = 'A,2,3,4,5,6,7,8,9,10,J,Q,K'.split(',');

  for (let i = 0; i < n; i++) {
    const id = hand[i]!;
    const c = card(id);
    cardPoints[i] = deadwoodValue(id, ctx);
    // A wild-rank card imposes no constraint on the prune: it can play as a
    // wild in any meld, so it can never rule one out.
    const flexible = c.printedJoker || (ctx.wildRank !== null && c.rank === ctx.wildRank);
    if (flexible) {
      // Contributes no constraint: it may play as a wild in any meld.
      cardSuitBit[i] = 0;
      cardRankBit[i] = 0;
    } else {
      cardSuitBit[i] = SUIT_BITS[c.suit as string] ?? 0;
      cardRankBit[i] = 1 << RANK_ORDER.indexOf(c.rank as string);
    }
  }

  for (let mask = 1; mask < total; mask++) {
    const low = 31 - Math.clz32(mask & -mask);
    lowIndex[mask] = low;
    const rest = mask ^ (1 << low);
    suitU[mask] = suitU[rest]! | cardSuitBit[low]!;
    rankU[mask] = rankU[rest]! | cardRankBit[low]!;
    size[mask] = size[rest]! + 1;
    value[mask] = value[rest]! + cardPoints[low]!;
  }

  const minSize = ctx.config.minMeldSize;
  const melds: Meld[] = [];
  for (let mask = 1; mask < total; mask++) {
    const sz = size[mask]!;
    if (sz < minSize) continue;
    const seqPossible = popcount(suitU[mask]!) <= 1;
    const setPossible = sz <= ctx.config.maxSetSize && popcount(rankU[mask]!) <= 1;
    if (!seqPossible && !setPossible) continue;

    const ids: CardId[] = [];
    for (let i = 0; i < n; i++) if (mask & (1 << i)) ids.push(hand[i]!);
    const meld = classifyMeld(ids, ctx);
    if (meld === null) continue;
    melds.push({
      mask,
      low: lowIndex[mask]!,
      isSeq: meld.kind !== 'set',
      isPure: meld.kind === 'pure-sequence',
      value: value[mask]!,
    });
  }
  return melds;
}

/**
 * Exact optimal grouping by bitmask DP.
 *
 * `best[mask][p][s]` is the minimum deadwood achievable over exactly the cards
 * in `mask`, having formed `p` pure sequences and `s` sequences (both capped at
 * what the winning condition requires — beyond that the counts are irrelevant).
 * With 13 cards this is 8192 × 6 states, which is instant and, unlike a
 * heuristic, is not going to disagree with a player who has spotted a better
 * arrangement than the server did.
 */
export function solveHand(hand: readonly CardId[], ctx: MeldContext): SolveResult {
  const cfg = ctx.config;
  const n = hand.length;
  const total = 1 << n;
  const PURE_CAP = cfg.minPureSequences;
  const SEQ_CAP = cfg.minSequences;
  const P = PURE_CAP + 1;
  const S = SEQ_CAP + 1;
  const STATES = P * S;

  const melds = enumerateMelds(hand, ctx);
  const meldsByLow: Meld[][] = Array.from({ length: n }, () => []);
  for (const m of melds) meldsByLow[m.low]!.push(m);

  const cardPoints = hand.map((id) => deadwoodValue(id, ctx));
  const handTotal = cardPoints.reduce((a, b) => a + b, 0);
  // One point-weighted unit per card, plus one count-unit for the card itself.
  const cardKey = cardPoints.map((p) => (p << POINT_SHIFT) | 1);

  const best = new Int32Array(total * STATES).fill(INF);
  // choice: -1 unreached, -2 lowest card left as deadwood, else meld index.
  const choice = new Int32Array(total * STATES).fill(-1);
  const from = new Int32Array(total * STATES).fill(-1);
  best[0] = 0;
  choice[0] = -2;

  const meldIndex = new Map<Meld, number>(melds.map((m, i) => [m, i]));

  for (let mask = 1; mask < total; mask++) {
    const low = 31 - Math.clz32(mask & -mask);
    const rest = mask ^ (1 << low);
    const restBase = rest * STATES;
    const base = mask * STATES;

    for (let st = 0; st < STATES; st++) {
      const prev = best[restBase + st]!;
      if (prev >= INF) continue;
      const cand = prev + cardKey[low]!;
      if (cand < best[base + st]!) {
        best[base + st] = cand;
        choice[base + st] = -2;
        from[base + st] = st;
      }
    }

    for (const m of meldsByLow[low]!) {
      if ((mask & m.mask) !== m.mask) continue;
      const sub = mask ^ m.mask;
      const subBase = sub * STATES;
      for (let st = 0; st < STATES; st++) {
        const prev = best[subBase + st]!;
        if (prev >= INF) continue;
        const p = Math.floor(st / S);
        const s = st % S;
        const np = m.isPure ? Math.min(PURE_CAP, p + 1) : p;
        const ns = m.isSeq ? Math.min(SEQ_CAP, s + 1) : s;
        const nst = np * S + ns;
        if (prev < best[base + nst]!) {
          best[base + nst] = prev;
          choice[base + nst] = meldIndex.get(m)!;
          from[base + nst] = st;
        }
      }
    }
  }

  const full = total - 1;
  const winState = PURE_CAP * S + SEQ_CAP;
  const winKey = best[full * STATES + winState]!;
  const declarableDeadwood = winKey < INF ? points(winKey) : null;
  // §3.3 — a declaration needs every card in a combination. Zero points is not
  // enough; a loose joker is worth nothing and would otherwise pass.
  const canDeclare = winKey === 0;

  // Tier 2 (§3.6): with a pure sequence but not two sequences, only the cards
  // outside a pure sequence count. Maximise the value shielded by pure runs.
  const pureCover = new Int32Array(total);
  const pureChoice = new Int32Array(total).fill(-2);
  const hasAnyPure = melds.some((m) => m.isPure);
  if (hasAnyPure) {
    for (let mask = 1; mask < total; mask++) {
      const low = 31 - Math.clz32(mask & -mask);
      const rest = mask ^ (1 << low);
      let bestCover = pureCover[rest]!;
      let pick = -2;
      for (const m of meldsByLow[low]!) {
        if (!m.isPure) continue;
        if ((mask & m.mask) !== m.mask) continue;
        const cand = m.value + pureCover[mask ^ m.mask]!;
        if (cand > bestCover) {
          bestCover = cand;
          pick = meldIndex.get(m)!;
        }
      }
      pureCover[mask] = bestCover;
      pureChoice[mask] = pick;
    }
  }

  let score: number;
  let arrangement: Arrangement;

  if (declarableDeadwood !== null) {
    score = Math.min(cfg.scoring.cap, declarableDeadwood);
    arrangement = reconstruct(hand, melds, choice, from, full, winState, STATES, S);
  } else if (hasAnyPure) {
    score = Math.min(cfg.scoring.cap, handTotal - pureCover[full]!);
    arrangement = reconstructPure(hand, melds, pureChoice, full);
  } else {
    score = Math.min(cfg.scoring.cap, handTotal);
    // Still show the player the best melds we found, even though none of them
    // shield any points — an empty tray tells them nothing.
    arrangement = bestEffortArrangement(hand, melds, best, choice, from, STATES, S, full);
  }

  const deadwoodByState: number[][] = [];
  const ungroupedByState: number[][] = [];
  for (let p = 0; p <= PURE_CAP; p++) {
    const pointsRow: number[] = [];
    const countRow: number[] = [];
    for (let s = 0; s <= SEQ_CAP; s++) {
      const key = best[full * STATES + (p * S + s)]!;
      pointsRow.push(key >= INF ? Infinity : points(key));
      countRow.push(key >= INF ? Infinity : ungrouped(key));
    }
    deadwoodByState.push(pointsRow);
    ungroupedByState.push(countRow);
  }

  return {
    points: score,
    declarableDeadwood,
    canDeclare,
    arrangement,
    sequencesShort: Math.max(0, SEQ_CAP - arrangement.sequences),
    pureShort: Math.max(0, PURE_CAP - arrangement.pureSequences),
    deadwoodByState,
    ungroupedByState,
  };
}

function meldsToGroups(hand: readonly CardId[], melds: Meld[], picked: number[]): Arrangement {
  const groups: Group[] = [];
  let used = 0;
  let sequences = 0;
  let pureSequences = 0;
  const ranked = picked
    .map((i) => melds[i]!)
    .sort((a, b) => rankMeld(a) - rankMeld(b));
  for (const m of ranked) {
    const g: Group = [];
    for (let i = 0; i < hand.length; i++) if (m.mask & (1 << i)) g.push(hand[i]!);
    groups.push(g);
    used |= m.mask;
    if (m.isSeq) sequences++;
    if (m.isPure) pureSequences++;
  }
  const deadwood: CardId[] = [];
  for (let i = 0; i < hand.length; i++) if (!(used & (1 << i))) deadwood.push(hand[i]!);
  return { groups, deadwood, sequences, pureSequences };
}

function rankMeld(m: Meld): number {
  if (m.isPure) return 0;
  if (m.isSeq) return 1;
  return 2;
}

function reconstruct(
  hand: readonly CardId[],
  melds: Meld[],
  choice: Int32Array,
  from: Int32Array,
  full: number,
  state: number,
  STATES: number,
  _S: number,
): Arrangement {
  const picked: number[] = [];
  let mask = full;
  let st = state;
  while (mask !== 0) {
    const idx = mask * STATES + st;
    const c = choice[idx]!;
    const prevState = from[idx]!;
    if (c === -2) {
      const low = 31 - Math.clz32(mask & -mask);
      mask ^= 1 << low;
    } else if (c >= 0) {
      picked.push(c);
      mask ^= melds[c]!.mask;
    } else {
      break;
    }
    st = prevState < 0 ? st : prevState;
  }
  return meldsToGroups(hand, melds, picked);
}

function reconstructPure(
  hand: readonly CardId[],
  melds: Meld[],
  pureChoice: Int32Array,
  full: number,
): Arrangement {
  const picked: number[] = [];
  let mask = full;
  while (mask !== 0) {
    const c = pureChoice[mask]!;
    if (c >= 0) {
      picked.push(c);
      mask ^= melds[c]!.mask;
    } else {
      const low = 31 - Math.clz32(mask & -mask);
      mask ^= 1 << low;
    }
  }
  return meldsToGroups(hand, melds, picked);
}

function bestEffortArrangement(
  hand: readonly CardId[],
  melds: Meld[],
  best: Int32Array,
  choice: Int32Array,
  from: Int32Array,
  STATES: number,
  S: number,
  full: number,
): Arrangement {
  let bestState = 0;
  let bestScore = Infinity;
  for (let st = 0; st < STATES; st++) {
    const key = best[full * STATES + st]!;
    if (key >= INF) continue;
    // Prefer more sequences, then less deadwood.
    const seqs = st % S;
    const score = points(key) - seqs * 1000;
    if (score < bestScore) {
      bestScore = score;
      bestState = st;
    }
  }
  return reconstruct(hand, melds, choice, from, full, bestState, STATES, S);
}

/**
 * Auto-sort (§6.3): suggested lanes covering the whole tray. Melds first, then
 * the remainder grouped by suit so the leftovers are still readable. This is
 * non-destructive at the UI layer — the previous manual arrangement is kept for
 * Undo.
 */
export function autoSort(hand: readonly CardId[], ctx: MeldContext): Group[] {
  const { arrangement } = solveHand(hand, ctx);
  const groups = arrangement.groups.map((g) => sortForDisplay(g, ctx));
  const rest = sortForDisplay(arrangement.deadwood, ctx);

  const bySuit = new Map<string, CardId[]>();
  for (const id of rest) {
    const c = card(id);
    const key = c.printedJoker ? 'JK' : (c.suit as string);
    const bucket = bySuit.get(key);
    if (bucket) bucket.push(id);
    else bySuit.set(key, [id]);
  }
  for (const bucket of bySuit.values()) groups.push(bucket);
  return groups.filter((g) => g.length > 0);
}

export interface Declaration {
  /** The card to place face down in the finish slot (§3.5). */
  finishCard: CardId;
  /** The remaining thirteen, grouped. */
  groups: Group[];
}

/**
 * Is this hand ready to declare, and with which card face down?
 *
 * A player about to declare is holding fourteen cards, and the question is not
 * "can all fourteen be melded" — it is "is there a card I can throw that leaves
 * a valid thirteen". Asking the first question of a fourteen-card hand answers
 * almost always no, and the answer it does give is unrelated to whether the
 * player has won.
 */
export function findDeclaration(
  hand: readonly CardId[],
  ctx: MeldContext,
): Declaration | null {
  if (hand.length <= ctx.config.handSize) {
    const solved = solveHand(hand, ctx);
    return solved.canDeclare ? { finishCard: '', groups: solved.arrangement.groups } : null;
  }

  const seen = new Set<string>();
  for (const candidate of hand) {
    // Two copies of the same card are interchangeable; solve once.
    const identity = candidate.split(':')[0]!;
    if (seen.has(identity)) continue;
    seen.add(identity);

    const remaining = hand.filter((c) => c !== candidate);
    const solved = solveHand(remaining, ctx);
    if (solved.canDeclare) {
      return { finishCard: candidate, groups: solved.arrangement.groups };
    }
  }
  return null;
}
